// src/pages/admin/manager/evaluaciones/EvaluacionesMetricasPage.tsx
import { useEffect, useState, Fragment } from 'react';
import {
  Stack,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Button,
  Tabs,
  Tab,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Grid,
  Collapse,
  IconButton,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../../../context/AuthContext';
import { useIsManager } from '../../../../hooks/useIsManager';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs, { Dayjs } from 'dayjs';
import {
  getFormularioMetrics,
  exportFormularioMetrics,
  getRespuestasIndividuales,
  getFormulario,
  getAsignaciones,
  type FormularioMetrics,
} from '../../../../api/formularios';

const COLORS = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#845EC2',
  '#4D8076',
  '#F65A83',
  '#2C73D2',
];

export default function EvaluacionesMetricasPage() {
  const { user, bootstrapped } = useAuth();
  const { isManager, loading: loadingManager } = useIsManager();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation<'translation'>();
  const { enqueueSnackbar } = useSnackbar();

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [metricas, setMetricas] = useState<FormularioMetrics | null>(null);
  const [evaluacion, setEvaluacion] = useState<any>(null);
  const [respuestas, setRespuestas] = useState<any[]>([]);
  const [expandedUsers, setExpandedUsers] = useState<Record<string, boolean>>({});
  const [asignaciones, setAsignaciones] = useState<any[]>([]);
  const [filters, setFilters] = useState({
    fechaDesde: null as Dayjs | null,
    fechaHasta: null as Dayjs | null,
    rol: '',
    usuarioId: undefined as number | undefined,
  });

  const loadAsignaciones = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getAsignaciones(Number(id));
      setAsignaciones(data);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Error al cargar asignaciones', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadEvaluacion = async () => {
    if (!id) return;
    try {
      const data = await getFormulario(Number(id));
      setEvaluacion(data);
    } catch (error) {
      // Error loading evaluacion
    }
  };

  const loadMetricas = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getFormularioMetrics(Number(id), {
        fechaDesde: filters.fechaDesde?.toISOString(),
        fechaHasta: filters.fechaHasta?.toISOString(),
        rol: filters.rol || undefined,
        usuarioId: filters.usuarioId,
      });
      setMetricas(data);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Error al cargar métricas', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadRespuestas = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getRespuestasIndividuales(Number(id), {
        page: 1,
        limit: 100,
        usuarioId: filters.usuarioId,
        rol: filters.rol || undefined,
      });
      setRespuestas(data.data || []);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Error al cargar respuestas', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const getUsuarioKey = (r: any) => {
    if (r.usuarioId !== undefined && r.usuarioId !== null) return String(r.usuarioId);
    if (r.usuarioEmail) return String(r.usuarioEmail);
    if (r.usuarioNombre) return String(r.usuarioNombre);
    if (r.id !== undefined && r.id !== null) return String(r.id);
    return `anon-${Math.random().toString(36).slice(2)}`;
  };

  const agruparRespuestasPorUsuario = (items: any[]) => {
    const map = new Map<
      string,
      { key: string; usuarioNombre: string; usuarioEmail?: string | null; respuestas: any[] }
    >();

    items.forEach((r) => {
      const key = getUsuarioKey(r);
      if (!map.has(key)) {
        map.set(key, {
          key,
          usuarioNombre: r.usuarioNombre || r.usuarioId || '-',
          usuarioEmail: r.usuarioEmail || null,
          respuestas: [],
        });
      }
      map.get(key)!.respuestas.push(r);
    });

    return Array.from(map.values());
  };

  const toggleExpanded = (key: string) => {
    setExpandedUsers((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    if (id && bootstrapped && !loadingManager && user && isManager) {
      loadEvaluacion();
      loadMetricas();
    }
  }, [id, bootstrapped, loadingManager, user, isManager]);

  useEffect(() => {
    if (id && tab === 1 && bootstrapped && !loadingManager && user && isManager) {
      loadRespuestas();
    }
  }, [id, tab, filters, bootstrapped, loadingManager, user, isManager]);

  useEffect(() => {
    if (id && tab === 2 && bootstrapped && !loadingManager && user && isManager) {
      loadAsignaciones();
    }
  }, [id, tab, bootstrapped, loadingManager, user, isManager]);

  if (!bootstrapped || loadingManager) {
    return null;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isManager) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleExport = async () => {
    if (!id) return;
    try {
      const filtros: any = {};
      if (filters.fechaDesde) filtros.fechaDesde = filters.fechaDesde.format('YYYY-MM-DD');
      if (filters.fechaHasta) filtros.fechaHasta = filters.fechaHasta.format('YYYY-MM-DD');
      if (filters.rol) filtros.rol = filters.rol;
      if (filters.usuarioId) filtros.usuarioId = filters.usuarioId;

      const blob = await exportFormularioMetrics(Number(id), filtros);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `evaluacion-${id}-metricas.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      enqueueSnackbar('Exportación iniciada', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar('Error al exportar', { variant: 'error' });
    }
  };

  // Calcular métricas específicas de evaluaciones
  const completados = asignaciones.filter((a: any) => a.completado).map((a: any) => a.completado);
  const calificaciones = completados
    .map((c: any) => c?.calificacion)
    .filter((cal: any) => cal !== null && cal !== undefined);
  const aprobados = completados.filter((c: any) => c?.aprobado === true).length;
  const reprobados = completados.filter((c: any) => c?.aprobado === false).length;
  const calificacionPromedio =
    calificaciones.length > 0
      ? calificaciones.reduce((a: number, b: number) => a + b, 0) / calificaciones.length
      : 0;

  // Distribución de calificaciones
  const distribucionCalificaciones = calificaciones.reduce(
    (acc: Record<number, number>, cal: number) => {
      const rango = Math.floor(cal);
      acc[rango] = (acc[rango] || 0) + 1;
      return acc;
    },
    {},
  );

  const distribucionCalificacionesData = Object.entries(distribucionCalificaciones).map(
    ([rango, count]) => ({
      rango: `${rango}-${Number(rango) + 1}`,
      cantidad: count,
    }),
  );

  // Datos de aprobados/reprobados
  const aprobadosReprobadosData = [
    { name: 'Aprobados', value: aprobados, color: '#00C49F' },
    { name: 'Reprobados', value: reprobados, color: '#FF8042' },
  ];

  // Evolución de calificaciones por intento
  const evolucionPorIntento = completados.reduce((acc: Record<number, number[]>, c: any) => {
    const intento = c?.intentoNumero || 1;
    if (!acc[intento]) acc[intento] = [];
    if (c?.calificacion !== null && c?.calificacion !== undefined) {
      acc[intento].push(c.calificacion);
    }
    return acc;
  }, {});

  const evolucionPorIntentoData =
    metricas?.analisisIntentos && metricas.analisisIntentos.length > 0
      ? metricas.analisisIntentos.map((i: any) => ({
          intento: `Intento ${i.intentoNumero}`,
          promedio: i.mediaCalificacion || 0,
          cantidad: i.total || 0,
        }))
      : Object.entries(evolucionPorIntento).map(([intento, calificaciones]) => ({
          intento: `Intento ${intento}`,
          promedio:
            (calificaciones as number[]).reduce((a, b) => a + b, 0) /
            (calificaciones as number[]).length,
          cantidad: (calificaciones as number[]).length,
        }));

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/manager/evaluaciones')}
          >
            Volver
          </Button>
          <Typography variant="h5">{evaluacion?.titulo || 'Métricas de la Evaluación'}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            startIcon={<FileDownloadIcon />}
            variant="outlined"
            onClick={handleExport}
            disabled={loading}
          >
            Exportar Excel
          </Button>
        </Stack>

        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Métricas" />
          <Tab label="Respuestas Individuales" />
          <Tab label="Asignaciones" />
        </Tabs>

        {tab === 0 && (
          <>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <DateTimePicker
                label="Fecha Desde"
                value={filters.fechaDesde}
                onChange={(v) => setFilters({ ...filters, fechaDesde: v })}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DateTimePicker
                label="Fecha Hasta"
                value={filters.fechaHasta}
                onChange={(v) => setFilters({ ...filters, fechaHasta: v })}
                slotProps={{ textField: { size: 'small' } }}
              />
              <TextField
                label="Rol"
                size="small"
                value={filters.rol}
                onChange={(e) => setFilters({ ...filters, rol: e.target.value })}
              />
              <Button variant="outlined" onClick={loadMetricas}>
                Aplicar Filtros
              </Button>
            </Stack>

            {loading ? (
              <Stack alignItems="center" p={4}>
                <CircularProgress />
              </Stack>
            ) : metricas ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Resumen General
                    </Typography>
                    <Typography variant="body1">
                      Total Asignados: {metricas.general?.asignacionesTotales || 0}
                    </Typography>
                    <Typography variant="body1">
                      Total Completados: {metricas.general?.completados || 0}
                    </Typography>
                    <Typography variant="body1">
                      Tasa de Completación: {(metricas.general?.tasaRespuesta || 0).toFixed(1)}%
                    </Typography>
                    {evaluacion?.puntuacionMaxima && (
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        Puntuación Máxima: {evaluacion.puntuacionMaxima}
                      </Typography>
                    )}
                    {evaluacion?.notaMinima && (
                      <Typography variant="body1">Nota Mínima: {evaluacion.notaMinima}</Typography>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Calificación Promedio
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {calificacionPromedio.toFixed(2)}
                    </Typography>
                    {evaluacion?.puntuacionMaxima && (
                      <Typography variant="body2" color="text.secondary">
                        de {evaluacion.puntuacionMaxima} puntos
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Tasa de Aprobación
                    </Typography>
                    <Typography
                      variant="h4"
                      color={aprobados > reprobados ? 'success.main' : 'error.main'}
                    >
                      {completados.length > 0
                        ? ((aprobados / completados.length) * 100).toFixed(1)
                        : 0}
                      %
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {aprobados} aprobados / {reprobados} reprobados
                    </Typography>
                  </Paper>
                </Grid>

                {aprobadosReprobadosData.some((d) => d.value > 0) && (
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Aprobados vs Reprobados
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={aprobadosReprobadosData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ percent = 0 }) => `${(percent * 100).toFixed(0)}%`}
                          >
                            {aprobadosReprobadosData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                )}

                {distribucionCalificacionesData.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Distribución de Calificaciones
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={distribucionCalificacionesData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="rango" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="cantidad" fill="#8884d8" name="Cantidad" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                )}

                {evolucionPorIntentoData.length > 0 && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Evolución de Calificaciones por Intento
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={evolucionPorIntentoData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="intento" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="promedio"
                            stroke="#8884d8"
                            name="Promedio"
                          />
                          <Line
                            type="monotone"
                            dataKey="cantidad"
                            stroke="#82ca9d"
                            name="Cantidad"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Métricas por Pregunta
                    </Typography>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Pregunta</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Total Respuestas</TableCell>
                          <TableCell>Media</TableCell>
                          <TableCell>Mediana</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {metricas.porPregunta?.map((m: any, idx: number) => (
                          <TableRow key={idx}>
                            <TableCell>{m.pregunta}</TableCell>
                            <TableCell>
                              <Chip label={m.tipo} size="small" />
                            </TableCell>
                            <TableCell>{m.totalRespuestas || 0}</TableCell>
                            <TableCell>{m.media?.toFixed(2) || '-'}</TableCell>
                            <TableCell>{m.mediana?.toFixed(2) || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>

                {metricas.coberturaCompetencias && metricas.coberturaCompetencias.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Cobertura por Competencia
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Competencia</TableCell>
                            <TableCell>Correctas</TableCell>
                            <TableCell>Total</TableCell>
                            <TableCell>%</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {metricas.coberturaCompetencias.map((c: any, idx: number) => (
                            <TableRow key={idx}>
                              <TableCell>{c.competencia}</TableCell>
                              <TableCell>{c.correctas}</TableCell>
                              <TableCell>{c.total}</TableCell>
                              <TableCell>{(c.porcentaje || 0).toFixed(1)}%</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Grid>
                )}

                {metricas.discriminacionPorPregunta &&
                  metricas.discriminacionPorPregunta.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Discriminación por Pregunta
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Pregunta</TableCell>
                              <TableCell>Índice</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {metricas.discriminacionPorPregunta.map((d: any, idx: number) => (
                              <TableRow key={idx}>
                                <TableCell>{d.pregunta}</TableCell>
                                <TableCell>{(d.indiceDiscriminacion || 0).toFixed(3)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Paper>
                    </Grid>
                  )}

                {metricas.sesgoPorRol && metricas.sesgoPorRol.length > 0 && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Sesgo por Rol
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Rol</TableCell>
                            <TableCell>Participantes</TableCell>
                            <TableCell>Media</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {metricas.sesgoPorRol.map((s: any, idx: number) => (
                            <TableRow key={idx}>
                              <TableCell>{s.rol}</TableCell>
                              <TableCell>{s.total}</TableCell>
                              <TableCell>{(s.mediaCalificacion || 0).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Paper sx={{ p: 4 }}>
                <Typography variant="body2" color="text.secondary" align="center">
                  No hay métricas disponibles
                </Typography>
              </Paper>
            )}
          </>
        )}

        {tab === 1 && (
          <>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <DateTimePicker
                label="Fecha Desde"
                value={filters.fechaDesde}
                onChange={(v) => setFilters({ ...filters, fechaDesde: v })}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DateTimePicker
                label="Fecha Hasta"
                value={filters.fechaHasta}
                onChange={(v) => setFilters({ ...filters, fechaHasta: v })}
                slotProps={{ textField: { size: 'small' } }}
              />
              <Button variant="outlined" onClick={loadRespuestas}>
                Aplicar Filtros
              </Button>
            </Stack>

            {loading ? (
              <Stack alignItems="center" p={4}>
                <CircularProgress />
              </Stack>
            ) : (
              <Paper>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Usuario</TableCell>
                      <TableCell>Total Respuestas</TableCell>
                      <TableCell>Última Fecha</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {respuestas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No hay respuestas
                        </TableCell>
                      </TableRow>
                    ) : (
                      agruparRespuestasPorUsuario(respuestas).map((u) => {
                        const open = Boolean(expandedUsers[u.key]);
                        const ultimaFecha = u.respuestas[0]?.createdAt
                          ? new Date(u.respuestas[0].createdAt).toLocaleDateString('es-ES')
                          : '-';
                        return (
                          <Fragment key={u.key}>
                            <TableRow>
                              <TableCell>
                                <IconButton size="small" onClick={() => toggleExpanded(u.key)}>
                                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </IconButton>
                              </TableCell>
                              <TableCell>{u.usuarioNombre}</TableCell>
                              <TableCell>{u.respuestas.length}</TableCell>
                              <TableCell>{ultimaFecha}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={4} sx={{ p: 0 }}>
                                <Collapse in={open} timeout="auto" unmountOnExit>
                                  <Box sx={{ p: 2 }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Pregunta</TableCell>
                                          <TableCell>Respuesta</TableCell>
                                          <TableCell>Fecha</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {u.respuestas.map((r: any, idx: number) => (
                                          <TableRow key={idx}>
                                            <TableCell>{r.preguntaTexto || '-'}</TableCell>
                                            <TableCell>{r.valorDisplay || '-'}</TableCell>
                                            <TableCell>
                                              {r.createdAt
                                                ? new Date(r.createdAt).toLocaleDateString('es-ES')
                                                : '-'}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </>
        )}

        {tab === 2 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Asignaciones de la Evaluación
            </Typography>
            {loading ? (
              <Stack alignItems="center" py={4}>
                <CircularProgress />
              </Stack>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Rol</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Calificación</TableCell>
                    <TableCell>Intento</TableCell>
                    <TableCell>Fecha Asignación</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {asignaciones.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No hay asignaciones para esta evaluación
                      </TableCell>
                    </TableRow>
                  ) : (
                    asignaciones.map((a: any) => (
                      <TableRow key={a.id}>
                        <TableCell>{a.usuarioNombre || `Usuario #${a.usuarioId}`}</TableCell>
                        <TableCell>{a.usuarioEmail || '-'}</TableCell>
                        <TableCell>{a.rol || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={a.activa ? 'Activa' : 'Desasignada'}
                            size="small"
                            color={a.activa ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {a.completado?.calificacion !== undefined
                            ? `${a.completado.calificacion} pts`
                            : '-'}
                        </TableCell>
                        <TableCell>{a.completado?.intentoNumero || '-'}</TableCell>
                        <TableCell>
                          {a.asignadoAt ? new Date(a.asignadoAt).toLocaleString('es-ES') : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </Paper>
        )}
      </Stack>
    </LocalizationProvider>
  );
}
